import { StreamChunk, ConversationResponse, StartConversationRequest, ContinueConversationRequest } from '../types/chat';

export interface ToolCall {
  name: string;
  status: string;
  description: string;
  started_at: string;
  completed_at?: string;
  error?: string;
}

export class ChatApiService {
  private baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8001';
  
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/health`);
      return response.ok;
    } catch {
      return false;
    }
  }

  async startConversation(request: StartConversationRequest): Promise<ConversationResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/conversation/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async continueConversation(request: ContinueConversationRequest): Promise<ConversationResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/conversation/continue`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async sendMessage(message: string, sessionId?: string): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/v1/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        session_id: sessionId,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async* streamChat(message: string, sessionId?: string): AsyncGenerator<StreamChunk> {
    const response = await fetch(`${this.baseUrl}/api/v1/chat/stream`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream'
      },
      body: JSON.stringify({ 
        message, 
        session_id: sessionId 
      })
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Stream not supported by browser');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = ''; // ✅ ADD: Buffer for accumulating partial chunks

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true }); // ✅ ADD: stream: true for proper decoding
        buffer += chunk; // ✅ ADD: Accumulate chunks
        
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // ✅ ADD: Keep the last incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            if (jsonStr && jsonStr !== '[DONE]') {
              try {
                yield JSON.parse(jsonStr) as StreamChunk;
              } catch (e) {
                console.warn('Failed to parse stream chunk:', jsonStr);
              }
            }
          }
        }
      }
      
      // ✅ ADD: Process any remaining data in buffer
      if (buffer.trim()) {
        const line = buffer.trim();
        if (line.startsWith('data: ')) {
          const jsonStr = line.slice(6).trim();
          if (jsonStr && jsonStr !== '[DONE]') {
            try {
              yield JSON.parse(jsonStr) as StreamChunk;
            } catch (e) {
              console.warn('Failed to parse final stream chunk:', jsonStr);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}

export const chatApi = new ChatApiService(); 